import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api, mensagemErro } from '../api';
import { useAuth } from '../auth';
import { Carregando, Erro, Icon, TagChip } from '../components/ui';
import { data, formatarWhatsapp, moeda, num, relativo } from '../format';
import { OpcoesStatus, StatusBadge, useStatusClientes } from '../status';
import { ModalEmailMassa, type AlvoMassa } from './EmailMassa';

export interface Tag { id: string; nome: string; cor: string; clientes?: number }
interface ClienteLinha {
  id: string; nome: string; empresa: string | null; whatsapp: string | null; status: string; statusId: string; statusNome: string; statusCor: string; cidade: string | null;
  ultimaCompra: string | null; valorUltimaCompra: string | null; ultimoContato: string | null;
  comunicacao: string; vendedorNome: string | null; adicionaisQtd?: number; tags: Tag[]; email: string | null; emailDescadastrado: boolean;
}
interface Lista { data: ClienteLinha[]; page: number; limit: number; total: number }

export const useTags = () => useQuery({ queryKey: ['tags'], queryFn: () => api<Tag[]>('/tags') });
export const useUsuarios = (ativo = true) =>
  useQuery({ queryKey: ['usuarios'], queryFn: () => api<Array<{ id: string; nome: string; perfil: string; status: string }>>('/usuarios'), enabled: ativo });

export function ClientesPage() {
  const { pode } = useAuth();
  const nav = useNavigate();
  const [sp, setSp] = useSearchParams();
  const [busca, setBusca] = useState(sp.get('busca') ?? '');
  const tags = useTags();
  const statusLista = useStatusClientes();
  const usuarios = useUsuarios(pode('CLIENTES_ATRIBUIR'));
  // seleção para e-mail em massa (guarda entre páginas) ou “todos do filtro”
  const [selecao, setSelecao] = useState<Map<string, string>>(new Map());
  const [todosDoFiltro, setTodosDoFiltro] = useState(false);
  const [massa, setMassa] = useState<AlvoMassa | null>(null);
  const podeMassa = pode('CAMPANHAS_CRIAR');

  // debounce da busca (300 ms)
  useEffect(() => {
    const t = setTimeout(() => {
      const n = new URLSearchParams(sp);
      if (busca.trim().length >= 2) n.set('busca', busca.trim()); else n.delete('busca');
      n.delete('page');
      if (n.toString() !== sp.toString()) setSp(n, { replace: true });
    }, 300);
    return () => clearTimeout(t);
  }, [busca]); // eslint-disable-line react-hooks/exhaustive-deps

  const limparSelecao = () => { setSelecao(new Map()); setTodosDoFiltro(false); };
  const filtro = (k: string, v: string | null) => {
    limparSelecao();
    const n = new URLSearchParams(sp);
    if (v) n.set(k, v); else n.delete(k);
    n.delete('page');
    setSp(n);
  };

  // status: grupo inteiro (status=ATIVO) ou um status específico (statusId)
  const filtroStatus = (v: string) => {
    limparSelecao();
    const n = new URLSearchParams(sp);
    n.delete('status'); n.delete('statusId'); n.delete('page');
    if (v.startsWith('g:')) n.set('status', v.slice(2)); else if (v) n.set('statusId', v);
    setSp(n);
  };

  const irPara = (p: number) => {
    const n = new URLSearchParams(sp);
    n.set('page', String(p));
    setSp(n);
  };

  const qs = sp.toString();
  const q = useQuery({ queryKey: ['clientes', qs], queryFn: () => api<Lista>(`/clientes?${qs}`), placeholderData: (p) => p });
  const page = Number(sp.get('page') ?? 1);
  const paginas = q.data ? Math.max(1, Math.ceil(q.data.total / q.data.limit)) : 1;
  const linhas = q.data?.data ?? [];
  const paginaMarcada = linhas.length > 0 && linhas.every((c) => selecao.has(c.id));
  const marcar = (c: ClienteLinha, on: boolean) => {
    setTodosDoFiltro(false);
    setSelecao((s0) => { const n = new Map(s0); if (on) n.set(c.id, c.nome); else n.delete(c.id); return n; });
  };
  const marcarPagina = (on: boolean) => {
    setTodosDoFiltro(false);
    setSelecao((s0) => { const n = new Map(s0); for (const c of linhas) { if (on) n.set(c.id, c.nome); else n.delete(c.id); } return n; });
  };
  const filtrosAtuais = () => {
    const f: Record<string, string> = {};
    for (const k of ['busca', 'status', 'statusId', 'tagId', 'comunicacao', 'semWhatsapp', 'semVendedor', 'vendedorId', 'comAdicionais', 'comEmail']) {
      const v = sp.get(k);
      if (v) f[k] = v;
    }
    return f;
  };
  const qtdSelecionada = todosDoFiltro ? q.data?.total ?? 0 : selecao.size;

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Clientes</h1>
          <span className="num muted small">{q.data ? `${num(q.data.total)} encontrados` : ' '}</span>
        </div>
        <div className="actions">
          {pode('CLIENTES_CRIAR') && <Link className="btn primary" to="/clientes/novo"><Icon name="mais" size={15} />Novo cliente</Link>}
        </div>
      </div>

      <div className="row" style={{ alignItems: 'center', gap: 10 }}>
        <label className="search">
          <Icon name="busca" />
          <span className="sr-only">Buscar clientes</span>
          <input value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Nome, empresa, WhatsApp ou e-mail" />
        </label>
        <select className="select" style={{ width: 190 }} aria-label="Status" value={sp.get('statusId') ?? (sp.get('status') ? `g:${sp.get('status')}` : '')}
          onChange={(e) => filtroStatus(e.target.value)}>
          <option value="">Todos os status</option>
          <OpcoesStatus lista={statusLista.data} grupos />
        </select>
        <select className="select" style={{ width: 170 }} aria-label="Tag" value={sp.get('tagId') ?? ''} onChange={(e) => filtro('tagId', e.target.value || null)}>
          <option value="">Todas as tags</option>
          {tags.data?.map((t) => <option key={t.id} value={t.id}>{t.nome}</option>)}
        </select>
        <select className="select" style={{ width: 190 }} aria-label="Comunicação" value={sp.get('comunicacao') ?? ''} onChange={(e) => filtro('comunicacao', e.target.value || null)}>
          <option value="">Qualquer comunicação</option><option value="PERMITIDO">Comunicação permitida</option><option value="NAO_CONTACTAR">Não contactar</option>
        </select>
        <label className="check"><input type="checkbox" checked={sp.get('semWhatsapp') === 'true'} onChange={(e) => filtro('semWhatsapp', e.target.checked ? 'true' : null)} />Sem WhatsApp</label>
        <label className="check"><input type="checkbox" checked={sp.get('comEmail') === 'true'} onChange={(e) => filtro('comEmail', e.target.checked ? 'true' : null)} />Com e-mail</label>
        {pode('CLIENTES_ATRIBUIR') && (
          <>
            <select className="select" style={{ width: 190 }} aria-label="Vendedor" value={sp.get('vendedorId') ?? ''}
              onChange={(e) => { filtro('vendedorId', e.target.value || null); if (!e.target.value) filtro('comAdicionais', null); }}>
              <option value="">Todos os vendedores</option>
              {usuarios.data?.filter((u) => u.status === 'ATIVO').map((u) => <option key={u.id} value={u.id}>{u.nome}</option>)}
            </select>
            {sp.get('vendedorId') && (
              <label className="check"><input type="checkbox" checked={sp.get('comAdicionais') === 'true'} onChange={(e) => filtro('comAdicionais', e.target.checked ? 'true' : null)} />Incluindo onde também atende</label>
            )}
            <label className="check"><input type="checkbox" checked={sp.get('semVendedor') === 'true'} onChange={(e) => filtro('semVendedor', e.target.checked ? 'true' : null)} />Sem vendedor</label>
          </>
        )}
      </div>

      {q.error && <Erro erro={mensagemErro(q.error)} />}
      <div className="card flush">
        {podeMassa && qtdSelecionada > 0 && (
          <div className="selecao-bar" role="region" aria-label="Clientes selecionados">
            <strong className="num" style={{ fontWeight: 600 }}>{num(qtdSelecionada)} {qtdSelecionada === 1 ? 'selecionado' : 'selecionados'}</strong>
            {!todosDoFiltro && paginaMarcada && (q.data?.total ?? 0) > selecao.size && (
              <button className="link-btn" style={{ color: 'var(--link)' }} onClick={() => { setTodosDoFiltro(true); setSelecao(new Map()); }}>
                Selecionar todos os {num(q.data!.total)} do filtro
              </button>
            )}
            <div className="actions">
              <button className="btn sm" onClick={limparSelecao}>Limpar</button>
              <button className="btn sm primary" onClick={() => setMassa(todosDoFiltro ? { filtros: filtrosAtuais() } : { clienteIds: [...selecao.keys()] })}>
                <Icon name="email" size={14} />Enviar e-mail
              </button>
            </div>
          </div>
        )}
        {q.isLoading ? <Carregando /> : q.data!.data.length === 0 ? (
          <div className="empty">
            Nenhum cliente encontrado.
            {pode('CLIENTES_CRIAR') && <Link className="btn primary" to="/clientes/novo">Cadastrar cliente</Link>}
          </div>
        ) : (
          <>
            <div className="table-wrap">
              <table>
                <thead><tr>
                  {podeMassa && <th style={{ width: 36 }}><input type="checkbox" aria-label="Selecionar a página" checked={todosDoFiltro || paginaMarcada} onChange={(e) => marcarPagina(e.target.checked)} /></th>}
                  <th>Cliente</th><th>WhatsApp</th><th>Tags</th><th>Status</th><th>Última compra</th><th>Último contato</th><th>Vendedor</th></tr></thead>
                <tbody>
                  {q.data!.data.map((c) => (
                    <tr key={c.id} className="click" onClick={() => nav(`/clientes/${c.id}`)}>
                      {podeMassa && (
                        <td onClick={(e) => e.stopPropagation()}>
                          <input type="checkbox" aria-label={`Selecionar ${c.nome}`} checked={todosDoFiltro || selecao.has(c.id)} disabled={todosDoFiltro}
                            onChange={(e) => marcar(c, e.target.checked)} />
                        </td>
                      )}
                      <td><Link to={`/clientes/${c.id}`} style={{ color: 'var(--text)', display: 'flex', flexDirection: 'column' }} onClick={(e) => e.stopPropagation()}>
                        <span style={{ fontWeight: 500 }}>{c.nome}</span>
                        <span className="small faint">{c.empresa ?? '—'}{c.email ? ` · ${c.email}` : ''}{c.emailDescadastrado ? ' (descadastrado)' : ''}</span></Link></td>
                      <td>{c.comunicacao === 'NAO_CONTACTAR'
                        ? <span style={{ display: 'flex', gap: 8, alignItems: 'center' }}><span className="num">{formatarWhatsapp(c.whatsapp)}</span><span className="badge red">Não contactar</span></span>
                        : (c.whatsapp ? <span className="num">{formatarWhatsapp(c.whatsapp)}</span> : <span className="badge amber">Completar</span>)}</td>
                      <td><span style={{ display: 'flex', gap: 4 }}>{c.tags.slice(0, 3).map((t) => <TagChip key={t.id} {...t} />)}</span></td>
                      <td><StatusBadge nome={c.statusNome} cor={c.statusCor} /></td>
                      <td className="num">{c.ultimaCompra ? `${data(c.ultimaCompra)} · ${moeda(c.valorUltimaCompra)}` : '—'}</td>
                      <td className="num">{relativo(c.ultimoContato)}</td>
                      <td style={c.vendedorNome ? undefined : { color: 'var(--amber-text)' }}>{c.vendedorNome ?? 'Sem vendedor'}{c.adicionaisQtd ? <span className="small faint" title="Outros vendedores também atendem"> +{c.adicionaisQtd}</span> : null}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="pager">
              <span className="num">Página {page} de {paginas}</span>
              <div className="actions">
                <button className="btn sm" disabled={page <= 1} onClick={() => irPara(page - 1)}>Anterior</button>
                <button className="btn sm" disabled={page >= paginas} onClick={() => irPara(page + 1)}>Próxima</button>
              </div>
            </div>
          </>
        )}
      </div>
      {massa && <ModalEmailMassa alvo={massa} onClose={() => setMassa(null)} />}
    </>
  );
}
